/**
 * Authentication Service
 * Manages user registration, login, logout, and token verification
 * Powered by centralized apiClient
 */

import apiClient, { setClientAuthToken } from './apiClient';

// Helper to set default Authorization header across apiClient
export const setAuthToken = (token) => {
  setClientAuthToken(token);
};

/**
 * Login user with email/password or Google auth
 * @param {Object} payload - {email, password?, signup_type: 'email'|'google', auth_token?}
 * @returns {Promise<Object>} Response with authToken
 */
export const loginUserAPI = async (payload) => {
  try {
    const { data } = await apiClient.post('/api/auth/login', payload);
    return data;
  } catch (error) {
    console.error('Login API error:', error);
    throw error.response?.data || error;
  }
};

/**
 * Sign up user with email/password or Google auth
 * @param {Object} payload - {email, password?, signup_type: 'email'|'google', auth_token?}
 * @returns {Promise<Object>} Response with authToken
 */
export const signUpUserAPI = async (payload) => {
  try {
    const { data } = await apiClient.post('/api/auth/register', payload);
    return data;
  } catch (error) {
    console.error('Signup API error:', error);
    throw error.response?.data || error;
  }
};

/**
 * Logout user
 * @returns {Promise<Object>} Response
 */
export const logoutUserAPI = async () => {
  try {
    const { data } = await apiClient.post('/api/auth/logout');
    return data;
  } catch (error) {
    console.error('Logout API error:', error);
    throw error.response?.data || error;
  }
};

/**
 * Verify token
 * @param {string} token - JWT token
 * @returns {Promise<Object>} Response with user data
 */
export const verifyTokenAPI = async (token) => {
  try {
    const config = token
      ? { headers: { Authorization: `Bearer ${token}` } }
      : {};
    const { data } = await apiClient.get('/api/auth/verify', config);
    return data;
  } catch (error) {
    console.error('Verify token API error:', error);
    throw error.response?.data || error;
  }
};

/**
 * Get authenticated API instance
 * @param {string} [token] - Optional JWT token override
 * @returns {AxiosInstance} Centralized axios instance
 */
export const getAuthenticatedAPI = (token) => {
  if (token) {
    setClientAuthToken(token);
  }
  return apiClient;
};

export const authAPI = apiClient;
export default apiClient;
